/**
 * The grid of an enumerated decision (funding, securities make-up parts, rate-position target and tenor, expense, technology level and preset): its options
 * in grid order, read off the CP-1 `Decisions` schema (a notch-or-literal union is not one, see `stringOptions`). A decision constraint stores a number (`DecisionConstraint.value`), so an enumerated decision's
 * row carries the option's index on this grid; `clampDecisions` turns it back into the option.
 */
import { z } from "zod";
import { Decisions } from "./cp1_domain";
import { decisionOfPath, decisionValueAt } from "./decision-paths";

/** The CP-1 schema of a decision path ("loans.cre.growth", "treasury.securitiesMix.type"); undefined for an unknown path. */
function decisionSchema(path: string): z.ZodType | undefined {
  return path.split(".").reduce<z.ZodType | undefined>(
    (node, key) => (node instanceof z.ZodObject ? (node.shape as Record<string, z.ZodType>)[key] : undefined), Decisions);
}

/**
 * The options a pure enum offers. A union of a notch and a literal (the rate-position target: -3 … +3 or "natural") is a numeric decision with one named setting: its row
 * carries the notch, never an index, or notch 0 ("Neutral 0%") would read as option 0, "natural", and EV-06 "Hedge now" would put on no hedge at all (F9.1 #9).
 */
const stringOptions = (schema: z.ZodType): readonly string[] | undefined => (schema instanceof z.ZodEnum ? (schema.options as readonly string[]) : undefined);

/** `path` ("treasury.funding", "operations.tech.preset") → its options; undefined for a numeric or unknown decision. */
export function enumDecisionOptions(path: string): readonly string[] | undefined {
  const schema = decisionSchema(path);
  return schema === undefined ? undefined : stringOptions(schema);
}

/** `path` ("loans.ci.growth", "capital.dividend") → a notch decision's ends; undefined for a decision that is not a bounded notch scale. */
export function notchDecisionBounds(path: string): { readonly min: number; readonly max: number } | undefined {
  const schema = decisionSchema(path);
  const number = schema instanceof z.ZodUnion ? (schema.options as z.ZodType[]).find((o) => o instanceof z.ZodNumber) : schema;
  if (!(number instanceof z.ZodNumber) || number.minValue === null || number.maxValue === null) return undefined;
  return { min: number.minValue, max: number.maxValue };
}

/** The notch a rate-position target stands at: a number as is, "natural" (no swaps) as 0; undefined for anything else. */
const notchOf = (v: unknown): number | undefined => (v === "natural" ? 0 : typeof v === "number" ? v : undefined);

/**
 * P4-D12, P5-D8 (21.2): the grid steps a guided round lets a stepped decision move either way, as the range of values a patch may write at `path`. A delta decision (the dividend,
 * patched as a step count at its own path) counts from 0; an absolute target (the rate position's `.target`, a notch -3..3 or "natural") counts from the round-start target in `seed`
 * (a missing one counts as natural, 0), so a bank holding +1 reaches +2 and not +3. Undefined for a path that is not a step (a tenor or any other leaf). One rule for the api guard and the screen.
 */
export function guidedStepRange(path: string, decisionId: string, seed: Decisions | undefined, limit: number): { readonly min: number; readonly max: number } | undefined {
  if (decisionOfPath(path) !== decisionId || (path !== decisionId && !path.endsWith(".target"))) return undefined;
  let held: unknown;
  try { held = seed === undefined ? undefined : decisionValueAt(seed, path); } catch { held = undefined; }
  const from = path === decisionId ? 0 : notchOf(held) ?? 0;
  return { min: from - limit, max: from + limit };
}

/** True when writing `value` at `path` moves the decision past its guided step limit; false for a value that is not a number or "natural", and for a path that is not a step. */
export function guidedStepExceeds(path: string, decisionId: string, value: unknown, seed: Decisions | undefined, limit: number): boolean {
  const range = guidedStepRange(path, decisionId, seed, limit), to = notchOf(value);
  return range !== undefined && to !== undefined && (to < range.min || to > range.max);
}
